import { NULL_OPERATORS } from '@/components/filterable-search-bar/DeviceFiltersUtils'
import type { FilterKey, FilterState } from '@/components/filterable-search-bar/type'
import { TIME_RANGE_OPTIONS, convertMsToReadable } from '@/config/utils'
import type { ColumnValueSource } from '@/types/columnValueSource'
import { getTypeFromClickhouseToTS, isAnyDateFieldType } from '@/utils/clickhouseTypes'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import type { GroupedFilterOperator, GroupedFilterOperatorOption, GroupedFilterValueOption } from './types'

export const SUGGESTIONS_CACHE_STALE_TIME_MS = 60_000

// Every operator picks values from the fetched options list (or takes no value for the null operators);
// there is no free-text entry. `contains` is intentionally absent — it can't be served as discrete options.
export const GROUPED_FILTER_OPERATORS: GroupedFilterOperatorOption[] = [
	{ value: 'in', labelSingle: 'is', labelMultiple: 'is any of' },
	{ value: 'not_in', labelSingle: 'is not', labelMultiple: 'is none of' },
	{ value: 'is_null', labelSingle: 'is null', labelMultiple: 'is null' },
	{ value: 'is_not_null', labelSingle: 'is not null', labelMultiple: 'is not null' },
]

export const NUMERIC_FILTER_OPERATORS: GroupedFilterOperatorOption[] = [
	{ value: '=', labelSingle: '=', labelMultiple: '=' },
	{ value: '!=', labelSingle: '!=', labelMultiple: '!=' },
	{ value: '>', labelSingle: '>', labelMultiple: '>' },
	{ value: '<', labelSingle: '<', labelMultiple: '<' },
	{ value: '>=', labelSingle: '>=', labelMultiple: '>=' },
	{ value: '<=', labelSingle: '<=', labelMultiple: '<=' },
	{ value: 'is_null', labelSingle: 'is null', labelMultiple: 'is null' },
	{ value: 'is_not_null', labelSingle: 'is not null', labelMultiple: 'is not null' },
]

export const BOOLEAN_FILTER_OPERATORS: GroupedFilterOperatorOption[] = [
	{ value: '=', labelSingle: 'is', labelMultiple: 'is' },
	{ value: '!=', labelSingle: 'is not', labelMultiple: 'is not' },
	{ value: 'is_null', labelSingle: 'is null', labelMultiple: 'is null' },
	{ value: 'is_not_null', labelSingle: 'is not null', labelMultiple: 'is not null' },
]

export const DERIVED_STATE_FILTER_OPERATORS: GroupedFilterOperatorOption[] = [
	{ value: 'in', labelSingle: 'is', labelMultiple: 'is any of' },
]

/** A field declaring its own options array is a derived device state column. */
export const isDerivedStateField = (field?: FilterKey): boolean => Boolean(field?.options && field.options.length > 0)

const defaultOperatorsForField = (
	field?: FilterKey,
	enableDataTypeOperators?: boolean,
): GroupedFilterOperatorOption[] => {
	if (enableDataTypeOperators) {
		if (field?.type === 'number') {
			return NUMERIC_FILTER_OPERATORS
		}
		if (field?.type === 'boolean') {
			return BOOLEAN_FILTER_OPERATORS
		}
	}
	return field && isDerivedStateField(field) ? DERIVED_STATE_FILTER_OPERATORS : GROUPED_FILTER_OPERATORS
}

export const getOperatorsForField = (
	field?: FilterKey,
	enableDataTypeOperators?: boolean,
): GroupedFilterOperatorOption[] => {
	const base = defaultOperatorsForField(field, enableDataTypeOperators)
	if (!field?.operators) {
		return base
	}
	const allowed = new Set<GroupedFilterOperator>(field.operators)
	const restricted = base.filter(option => allowed.has(option.value))
	// An allow-list disjoint from the type-derived set (a `number` field allowing only `in`/`not_in`) would
	// otherwise leave the field with no operator to pick at all. Treat it as undeclared instead.
	return restricted.length > 0 ? restricted : base
}

export const DEFAULT_GROUPED_OPERATOR: GroupedFilterOperator = 'in'

export const isNullOperator = (operator: GroupedFilterOperator): boolean => NULL_OPERATORS.includes(operator)

export const operatorLabel = (
	operator: GroupedFilterOperator,
	valueCount: number,
	fieldType?: 'string' | 'number' | 'boolean',
	field?: FilterKey,
): string => {
	const optionList = field
		? getOperatorsForField(field, Boolean(fieldType))
		: fieldType === 'number'
			? NUMERIC_FILTER_OPERATORS
			: fieldType === 'boolean'
				? BOOLEAN_FILTER_OPERATORS
				: GROUPED_FILTER_OPERATORS
	const option =
		optionList.find(o => o.value === operator) ||
		GROUPED_FILTER_OPERATORS.find(o => o.value === operator) ||
		NUMERIC_FILTER_OPERATORS.find(o => o.value === operator) ||
		BOOLEAN_FILTER_OPERATORS.find(o => o.value === operator) ||
		DERIVED_STATE_FILTER_OPERATORS.find(o => o.value === operator)
	if (!option) {
		return operator
	}
	return valueCount > 1 ? option.labelMultiple : option.labelSingle
}

/**
 * Translate a grouped FilterKey into the filter-options `column` id the value API expects. FilterKey ids
 * are bare (`city`, `mode`); the endpoint needs the `metadata.`/`state.` prefix (see
 * `deviceColumnIdToFilterColumn`). The query builder still uses the bare id via FilterState.type, so this
 * mapping is only for value fetching.
 */
export const fieldToColumnSourceId = (field: FilterKey): string => {
	// Org-level pseudo-columns keep their bare id (the filter-options endpoint / project name list expect it).
	if (
		field.id === 'id' ||
		field.id === 'uid' ||
		field.id === 'project' ||
		field.id === 'tenantName' ||
		field.id === 'deviceAttachment'
	) {
		return field.id === 'tenantName' ? 'project' : field.id
	}
	if (field.source === 'deviceState') {
		return `state.${field.id}`
	}
	return `metadata.${field.id}`
}

export const fieldColumnType = (field: FilterKey) =>
	field.clickhouseType ? getTypeFromClickhouseToTS(field.clickhouseType) : 'string'

export const isTimeRangeField = (field: FilterKey): boolean =>
	field.id === 'heartbeat' || isAnyDateFieldType(field.clickhouseType)

export const allowsMultipleConditions = (field: FilterKey, enableDataTypeOperators?: boolean): boolean =>
	Boolean(enableDataTypeOperators) && field.type === 'number' && !isTimeRangeField(field)

// 'custom' is dropped from the presets: the panel offers a custom duration through its own number + unit inputs.
export const TIME_RANGE_PRESETS = TIME_RANGE_OPTIONS.filter(option => option.value !== 'custom')

export type CustomTimeUnit = 'minutes' | 'hours' | 'days'
const CUSTOM_UNIT_MS: Record<CustomTimeUnit, number> = { minutes: 60_000, hours: 3_600_000, days: 86_400_000 }

/** A stored time-range value is either a preset key ('1hour') or a custom `${ms}ms` string. */
export const isCustomTimeValue = (value: string): boolean => value.endsWith('ms')
export const customTimeToValue = (num: number, unit: CustomTimeUnit): string => `${num * CUSTOM_UNIT_MS[unit]}ms`
export const parseCustomTimeValue = (value: string): { num: number; unit: CustomTimeUnit } => {
	const ms = Number.parseInt(value.replace('ms', ''), 10) || 0
	if (ms >= CUSTOM_UNIT_MS.days && ms % CUSTOM_UNIT_MS.days === 0) {
		return { num: ms / CUSTOM_UNIT_MS.days, unit: 'days' }
	}
	if (ms >= CUSTOM_UNIT_MS.hours && ms % CUSTOM_UNIT_MS.hours === 0) {
		return { num: ms / CUSTOM_UNIT_MS.hours, unit: 'hours' }
	}
	return { num: Math.max(1, Math.round(ms / CUSTOM_UNIT_MS.minutes)), unit: 'minutes' }
}

/** Human label for a stored time-range value ('1hour' → "1 hour", '3600000ms' → "1 hour"). */
export const timeRangeLabel = (value: string): string => convertMsToReadable(value)

/** Map the grouped-bar operator onto the FilterState operator vocabulary understood by the existing builder. */
const toFilterStateOperator = (operator: GroupedFilterOperator): string => {
	switch (operator) {
		case 'in':
			return 'is'
		case 'not_in':
			return 'is_not'
		case 'is_null':
			return 'is_null'
		case 'is_not_null':
			return 'is_not_null'
		case '=':
			return '='
		case '!=':
			return '!='
		case '>':
			return '>'
		case '<':
			return '<'
		case '>=':
			return '>='
		case '<=':
			return '<='
	}
}

let conditionCounter = 0
/**
 * Suffixed like group ids: persisted conditions outlive the page, so a restarted counter must not collide.
 * Shared with URL restore, which re-keys conditions carrying a missing or duplicated id.
 */
export const buildConditionId = (): string => {
	conditionCounter += 1
	return `cond-${conditionCounter}-${Date.now().toString(36)}`
}

/** Reuses the existing FilterState shape so the query builder and all conversion helpers apply unchanged. */
export const buildCondition = (
	field: FilterKey,
	operator: GroupedFilterOperator,
	values: string[],
	options?: { id?: string },
): FilterState => {
	const nullOp = isNullOperator(operator)
	return {
		id: options?.id ?? buildConditionId(),
		field: field.id,
		fieldLabel: field.label,
		operator: toFilterStateOperator(operator),
		operatorLabel: toFilterStateOperator(operator),
		value: nullOp ? [] : values,
		valueLabels: nullOp ? [] : values.map(v => field.formatValueLabel?.(v) ?? v),
		type: field.source === 'deviceState' ? 'state' : 'metadata',
		isNullOperator: nullOp,
		columnType: fieldColumnType(field),
	}
}

export const conditionOperator = (condition: FilterState): GroupedFilterOperator => {
	const op = condition.operatorLabel || condition.operator
	switch (op) {
		case 'is_not':
			return 'not_in'
		case 'not=':
		case '!=':
			return '!='
		case 'is_null':
			return 'is_null'
		case 'is_not_null':
			return 'is_not_null'
		case '=':
		case 'equals':
			return '='
		case '>':
			return '>'
		case '<':
			return '<'
		case '>=':
			return '>='
		case '<=':
			return '<='
		default:
			return 'in'
	}
}

export const conditionValues = (condition: FilterState): string[] => {
	if (Array.isArray(condition.value)) {
		return condition.value.filter(v => v !== null && v !== undefined).map(v => String(v))
	}
	if (condition.value === null || condition.value === undefined) {
		return []
	}
	return [String(condition.value)]
}

/**
 * The condition-side counterpart of `isTimeRangeField`, shared by the query builder, the client badge
 * matcher and conflict detection so a stored window cannot be read three different ways. The guards
 * matter: a nullness check has no window, and an already-converted epoch must not convert twice.
 */
export const isRelativeTimeCondition = (condition: FilterState): boolean => {
	if (condition.field !== 'heartbeat' && !isAnyDateFieldType(condition.columnType)) {
		return false
	}
	if (isNullOperator(conditionOperator(condition))) {
		return false
	}
	const [value] = conditionValues(condition)
	return value !== undefined && Number.isNaN(Number(value))
}

/**
 * Lazily fetch selectable values for a field via the shared `ColumnValueSource` (the same filter-options
 * fetch path the old bar uses — serves metadata AND device_state columns). The field id is prefixed to the
 * column id the endpoint expects (`state.`/`metadata.`).
 */
export function useColumnValues(
	field: FilterKey | null,
	search: string,
	source: ColumnValueSource | undefined,
	enabled: boolean,
) {
	const columnSourceId = field ? fieldToColumnSourceId(field) : ''
	const supported = Boolean(field && source?.isColumnSupported(columnSourceId))
	const perPage = source?.perPage ?? 1000

	const query = useInfiniteQuery({
		queryKey: [...(source?.queryKey ?? []), 'grouped', columnSourceId, search],
		enabled: enabled && supported && Boolean(source),
		initialPageParam: 1,
		queryFn: async ({ pageParam, signal }) => {
			if (!source || !field) {
				return { values: [], count: 0 }
			}
			return source.fetch({ columnId: columnSourceId, search, page: pageParam, perPage, signal })
		},
		getNextPageParam: (lastPage, allPages) => {
			const loaded = allPages.reduce((sum, page) => sum + page.values.length, 0)
			return loaded < lastPage.count ? allPages.length + 1 : undefined
		},
		staleTime: SUGGESTIONS_CACHE_STALE_TIME_MS,
	})

	const options = useMemo<GroupedFilterValueOption[]>(() => {
		const rawPages = query.data?.pages
		const pages = Array.isArray(rawPages) ? rawPages : []
		const seen = new Set<string>()
		const out: GroupedFilterValueOption[] = []
		for (const page of pages ?? []) {
			for (const raw of page.values ?? []) {
				const str = String(raw)
				if (str.trim() === '' || seen.has(str)) {
					continue
				}
				seen.add(str)
				out.push({ value: str, label: field?.formatValueLabel?.(str) ?? str })
			}
		}
		return out
	}, [query.data, field])

	return {
		supported,
		options,
		isLoading: supported && query.isPending && enabled,
		hasNextPage: Boolean(query.hasNextPage),
		fetchNextPage: query.fetchNextPage,
		isFetchingNextPage: query.isFetchingNextPage,
	}
}

/** Resolve a backend column source id (e.g. 'id', 'metadata.reg_no', 'state.speed') to a front-end FilterKey. */
export function findFieldForColumnSourceId(fields: FilterKey[], columnSourceId: string): FilterKey | undefined {
	const normalized = columnSourceId.toLowerCase()
	return fields.find(field => {
		const sourceId = fieldToColumnSourceId(field).toLowerCase()
		if (sourceId === normalized || field.id.toLowerCase() === normalized) {
			return true
		}
		if (normalized.startsWith('metadata.') && field.source === 'metadata') {
			return field.id.toLowerCase() === normalized.slice('metadata.'.length)
		}
		if (normalized.startsWith('state.') && field.source === 'deviceState') {
			return field.id.toLowerCase() === normalized.slice('state.'.length)
		}
		return false
	})
}

export function useAllColumnSuggestions(fields: FilterKey[], search: string, source: ColumnValueSource | undefined) {
	const enabled = Boolean(source?.fetchAllSuggestions && search.length > 0)
	const query = useQuery({
		queryKey: [...(source?.queryKey ?? []), 'allSuggestions', search],
		enabled,
		queryFn: async ({ signal }) => {
			if (!source?.fetchAllSuggestions) {
				return []
			}
			return source.fetchAllSuggestions({ search, signal })
		},
		staleTime: SUGGESTIONS_CACHE_STALE_TIME_MS,
	})

	const suggestions = useMemo(() => {
		if (!search || !query.data) {
			return []
		}
		const results: Array<{ field: FilterKey; value: string; label: string; key: string }> = []
		const seen = new Set<string>()
		for (const item of query.data) {
			const field = findFieldForColumnSourceId(fields, item.fieldId)
			if (!field) {
				continue
			}
			const uniqueKey = `${field.id}:${item.value}`
			if (seen.has(uniqueKey)) {
				continue
			}
			seen.add(uniqueKey)
			results.push({
				field,
				value: item.value,
				label: field.formatValueLabel?.(item.value) ?? item.value,
				key: uniqueKey,
			})
		}
		return results
	}, [search, query.data, fields])

	return {
		suggestions,
		isLoading: enabled && query.isPending,
	}
}
